import type { Team, TournamentBracketPublicTournament } from 'modules/client/tournaments/domain/types';
import type { Team as BracketTeam } from 'modules/teams/domain/interfaces/team';
import { Round } from 'modules/tournaments/domain/interfaces/round';
import type { TournamentBracket } from 'modules/tournaments/domain/interfaces/tournament-bracket';
import { RoundBrackets } from 'modules/tournaments/ui/components/form-parts/stages/stage-grid/stage-brackets/round-brackets';
import {
  RoundBracketsContainer,
  RoundHeaders,
} from 'modules/tournaments/ui/components/form-parts/stages/stage-grid/stage-brackets/stage-brackets-form.styles';
import React, { useMemo } from 'react';
import UDText from '../../../../ud-ui/components/text';

interface IPropsTournamentBracket {
  brackets: TournamentBracketPublicTournament[];
  stageId?: number;
  teams: Team[];
}

function getBracketsByRound(entities: TournamentBracketPublicTournament[], stageId: number | null) {
  if (!stageId) {
    return {};
  }

  const bracketsByRound = entities
    .filter((bracket) => bracket.stageId === stageId)
    .reduce<Record<number, TournamentBracket[]>>((results, bracket) => {
      const { roundId } = bracket;
      const roundBrackets = results[roundId] || [];

      return {
        ...results,
        [roundId]: [...roundBrackets, bracket],
      };
    }, {});

  const bracketPairsByRound: Record<number, Array<TournamentBracket[]>> = {};

  Object.keys(bracketsByRound).forEach((roundId) => {
    const key = Number(roundId);
    const roundBrackets = bracketsByRound[key];

    const pairs = [];
    for (let i = 0; i < roundBrackets.length; i += 2) {
      if (i + 1 < roundBrackets.length) {
        pairs.push([roundBrackets[i], roundBrackets[i + 1]]);
      } else {
        pairs.push([roundBrackets[i]]);
      }
    }

    bracketPairsByRound[key] = pairs;
  });

  return bracketPairsByRound;
}

function convertorTeam(teams: Team[]): BracketTeam[] {
  return teams.map((team) => ({
    name: team.name,
    shortName: team.shortName,
    id: team.id,
    logo: { id: team.id, url: team.logo },
    players: [],
  }));
}

function TournamentBracketTree(props: IPropsTournamentBracket) {
  const { brackets, stageId, teams } = props;
  const rounds: Round[] = brackets
    .reduce((array, bracket) => {
      const isHas = array.find((item) => item.id === bracket.round.id);
      if (!isHas) {
        array.push({
          stageId: bracket.stageId,
          name: bracket.round.name,
          id: bracket.round.id,
          matchIds: bracket.match?.id ? [bracket.match.id] : [],
          createdAt: new Date(),
          updatedAt: new Date(),
        });
      } else {
        isHas.matchIds.push(bracket.id);
      }

      return array;
    }, [] as Round[])
    .reverse();

  const bracketsByRound = getBracketsByRound(brackets, stageId ?? null);

  const convertedTeams = useMemo(() => {
    return convertorTeam(teams);
  }, [teams]);

  return (
    <div className="overflow-auto rounded-lg">
      <div className="min-w-[1248px] bg-white shadow-md">
        <div className="mb-4 bg-emerald-600 px-6 py-4 text-white">
          <RoundHeaders className="flex-center" rounds={rounds.length}>
            {rounds.map((round) => (
              <UDText className="round-title" type="headline" color="inherit">
                {round.name}
              </UDText>
            ))}
          </RoundHeaders>
        </div>

        <RoundBracketsContainer className="px-6">
          {rounds.map((round, index) => (
            <RoundBrackets
              key={round.id}
              className={`round round-${index + 1}`}
              teams={convertedTeams}
              bracketPairs={bracketsByRound[round.id]}
              color="gray"
            />
          ))}
        </RoundBracketsContainer>
      </div>
    </div>
  );
}

export default TournamentBracketTree;
